import { useQueryClient } from '@tanstack/react-query'
import { useAddChecklistTask } from 'api/aws/checklists/useBatchAddChecklistTask'
import { useCallback, useState } from 'react'
import { ChecklistInput, OrganizationTableItem } from 'types'

// Pure functions that don't need React hooks - defined outside component for stability
const selectChecklist = (checklists: any[], orgPK: string, type: string) =>
  (checklists || []).find(
    (ch: any) => (ch?.PK === orgPK || ch?.orgPK === orgPK) && (ch?.type === type || ch?.type === 'General')
  ) || null

const normalizeRows = (checklist: any) => {
  if (!checklist) return []
  const rows =
    checklist.sections?.flatMap((section: any) =>
      (section.items || []).map((it: any, idx: number) => ({
        orderIndex: it.itemIndex ?? idx,
        name: it.title ?? it.name,
        role: it.role ?? '',
        status: it.status ?? 'Not Started',
        sectionIndex: section.sectionIndex ?? 0,
        itemId: it.id ?? it.itemId ?? it.key,
        assigneeIds: it.assigneeIds ?? [],
        dependencies: Array.isArray(it.dependencies) ? it.dependencies : [],
      }))
    ) || []
  rows.sort((a: any, b: any) => a.orderIndex - b.orderIndex)
  const statusById = new Map(rows.map(r => [r.itemId, r.status]))
  return rows.map(r => ({
    ...r,
    canEdit: !r.dependencies?.length || r.dependencies.every((dep: string) => statusById.get(dep) === 'Done'),
  }))
}

const summarizeProgress = (rows: any[]) => {
  const total = rows.length
  const done = rows.filter(r => r.status === 'Done').length
  const pct = total ? Math.round((done / total) * 100) : 0
  const byRole = rows.reduce((acc: any, r: any) => {
    const key = r.role || '—'
    if (!acc[key]) acc[key] = { done: 0, total: 0 }
    acc[key].total += 1
    if (r.status === 'Done') acc[key].done += 1
    return acc
  }, {})
  return { total, done, pct, byRole }
}

const shallowEq = (a: any, b: any) =>
  a?.total === b?.total &&
  a?.done === b?.done &&
  a?.pct === b?.pct &&
  JSON.stringify(a?.byRole ?? {}) === JSON.stringify(b?.byRole ?? {})

const genTempId = () => `tmp_${Math.random().toString(36).slice(2, 9)}`

export const useNcoHelpers = (
  deal: OrganizationTableItem,
  activeIds?: { PK: string; SK: string } | null,
  currentChecklist?: any
) => {
  const qc = useQueryClient()
  const [draftRows, setDraftRows] = useState<any[]>([])
  const { mutate: addChecklistTask, isPending: _addingTasks } = useAddChecklistTask()

  const addItemToCache = useCallback(
    ({
      key,
      ids,
      item,
      atEnd = true,
    }: {
      key: readonly [string, string]
      ids: { PK: string; SK: string }
      item: any
      atEnd?: boolean
    }) => {
      const prev = qc.getQueryData<any[]>(key) || []
      const next = prev.map(ch => {
        if (ch.PK !== ids.PK || ch.SK !== ids.SK) return ch
        const sections = ch.sections?.length
          ? ch.sections
          : [{ sectionIndex: 0, title: ch.title ?? 'Onboarding', items: [] }]
        const s0 = sections[0]
        const items = Array.isArray(s0.items) ? [...s0.items] : []
        const insert = atEnd ? [...items, item] : [item, ...items]
        const s0next = { ...s0, items: insert }
        return { ...ch, sections: [s0next, ...sections.slice(1)] }
      })
      qc.setQueryData(key, next)
      return { key, prev }
    },
    [qc]
  )

  const discardDraftRow = useCallback((draft: any) => {
    setDraftRows(prev => prev.filter(d => d.itemId !== draft.itemId))
  }, [])

  const patchDraftRow = useCallback((itemId: string, patch: Partial<any>) => {
    setDraftRows(prev => prev.map(d => (d.itemId === itemId ? { ...d, ...patch } : d)))
  }, [])

  const handleAddRow = useCallback(() => {
    if (!activeIds) return

    // find last existing itemId (seeded + drafts)
    const seeded =
      currentChecklist?.sections?.[0]?.items
        ?.slice()
        ?.sort((a: any, b: any) => (a.itemIndex ?? 0) - (b.itemIndex ?? 0)) || []
    const lastSeededId = seeded.length
      ? seeded[seeded.length - 1].id ?? seeded[seeded.length - 1].itemId ?? seeded[seeded.length - 1].key
      : null

    setDraftRows(prev => {
      const lastDraftId = prev.length ? prev[prev.length - 1].itemId : null
      const prevId = lastDraftId ?? lastSeededId ?? null
      const orderIndex = (seeded.length ?? 0) + prev.length

      const draft = {
        _isDraft: true,
        sectionIndex: 0,
        itemIndex: orderIndex,
        itemId: genTempId(),
        name: '',
        title: '',
        role: '',
        status: 'Not Started',
        assigneeIds: [],
        dependencies: prevId ? [prevId] : [],
      }
      return [...prev, draft]
    })
  }, [activeIds, currentChecklist?.sections])

  const saveDraftRow = useCallback(
    (draft: any) => {
      if (!activeIds) return
      const PK = activeIds.PK
      const SK = activeIds.SK
      const key = ['checklists', deal?.PK] as const

      const newItemId = draft.itemId.startsWith('tmp_') ? `custom_${Date.now()}` : draft.itemId

      // optimistic insert
      const ctx = addItemToCache({
        key,
        ids: activeIds,
        item: {
          id: newItemId,
          itemId: newItemId,
          key: newItemId,
          title: draft.name || draft.title || 'Untitled Task',
          name: draft.name || draft.title || 'Untitled Task',
          role: draft.role || '',
          status: draft.status || 'Not Started',
          dependencies: [],
          itemIndex: draft.itemIndex ?? 0,
          assigneeIds: draft.assigneeIds ?? [],
        },
      })

      const newTask: ChecklistInput = {
        PK,
        SK,
        sectionIndex: 0,
        itemId: newItemId,
        key: newItemId,
        name: draft.name || draft.title || 'Untitled Task',
        role: draft.role || '',
        status: (draft.status as any) || 'Not Started',
        dependencies: [],
        itemIndex: draft.itemIndex ?? 0,
        assigneeIds: draft.assigneeIds ?? [],
      }

      addChecklistTask([newTask], {
        onSuccess: () => discardDraftRow(draft),
        onError: () => qc.setQueryData(ctx.key, ctx.prev),
      })
    },
    [activeIds, deal?.PK, addItemToCache, addChecklistTask, discardDraftRow, qc]
  )

  const invalidateOnlyChecklists = useCallback(
    () =>
      qc.invalidateQueries({
        predicate: q => Array.isArray(q.queryKey) && q.queryKey[0] === 'checklists',
      }),
    [qc]
  )
  return {
    draftRows,
    genTempId,
    addItemToCache,
    handleAddRow,
    saveDraftRow,
    discardDraftRow,
    patchDraftRow,
    invalidateOnlyChecklists,
    // Pure functions (stable references - defined outside hook)
    selectChecklist,
    normalizeRows,
    summarizeProgress,
    shallowEq,
  }
}
